import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchBookmarks } from '../../api/clientBookmarks';
import BookmarkButton from '../../components/explore/BookmarkButton';
import SalonPreviewModal from '../../components/explore/SalonPreviewModal';
import AudienceBadge from '../../components/salon/AudienceBadge';
import DashboardShell from '../../components/ui/DashboardShell';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';

const ClientBookmarksPage = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [previewId, setPreviewId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['client-bookmarks', page, pageSize],
    queryFn: () => fetchBookmarks({ page, limit: pageSize }),
  });

  const salons = data?.data || [];
  const pagination = data?.pagination;

  return (
    <DashboardShell title={t('client.bookmarks.title')} subtitle={t('client.bookmarks.subtitle')}>
      {isLoading && <p className="text-zinc-500">{t('common.loading')}</p>}

      {!isLoading && salons.length === 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white p-10 text-center dark:border-zinc-800 dark:bg-zinc-900">
          <p className="text-4xl">♡</p>
          <p className="mt-3 font-medium">{t('client.bookmarks.emptyTitle')}</p>
          <p className="mt-1 text-sm text-zinc-500">{t('client.bookmarks.emptyDesc')}</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {salons.map((salon) => (
          <article
            key={salon.tenantId}
            className="group relative overflow-hidden rounded-xl border border-zinc-200 bg-white transition hover:border-glow-500/50 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900"
          >
            <div className="absolute right-2 top-2 z-10">
              <BookmarkButton tenantId={salon.tenantId} size="sm" />
            </div>
            <button
              type="button"
              onClick={() => setPreviewId(salon.tenantId)}
              className="block w-full text-left"
            >
              <div className="relative h-36 bg-zinc-100 dark:bg-zinc-800">
                {salon.coverImage || salon.logo ? (
                  <img src={salon.coverImage || salon.logo} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-zinc-500">{t('common.noImage')}</div>
                )}
              </div>
              <div className="p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold group-hover:text-glow-600 dark:group-hover:text-glow-300">
                    {salon.businessName}
                  </p>
                  <AudienceBadge audienceType={salon.audienceType} />
                </div>
                {salon.tagline && (
                  <p className="mt-1 line-clamp-2 text-xs text-zinc-500">{salon.tagline}</p>
                )}
                <p className="mt-2 text-sm text-amber-500">
                  ★ {salon.averageRating || '—'} · {t('explore.reviewsCount', { count: salon.totalReviews || 0 })}
                </p>
                <p className="mt-3 text-xs font-medium text-glow-600 dark:text-glow-400">
                  {t('client.bookmarks.openPreview')} →
                </p>
              </div>
            </button>
          </article>
        ))}
      </div>

      <PaginatedFooter
        pagination={pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />

      <SalonPreviewModal tenantId={previewId} open={!!previewId} onClose={() => setPreviewId(null)} />
    </DashboardShell>
  );
};

export default ClientBookmarksPage;
